import type { BurnRateDistributionStrategy, BurnRateGranularity, ProjectPhase, IProject } from '@pulse/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createDeliveryProject, type CreateDeliveryProjectInput } from '../api/delivery-project.api';
import {
  addStaffing,
  destaffUser,
  editStaffingDates,
  fetchProject,
  fetchProjectBurnRate,
  fetchProjectNotes,
  fetchProjectPrerequisites,
  fetchProjects,
  fetchProjectsPaginated,
  fetchStaffingHistory,
  removeSowAttachment,
  transitionProject,
  updateProject,
  updateProjectFinance,
  updateProjectFundedFlags,
  updateProjectNote,
  updateProjectPrerequisite,
  updateStaffingAllocation,
  uploadSowAttachment,
  createProjectNote,
  type AddStaffingInput,
  type EditStaffingDatesInput,
  type ProjectListFilters,
  type UpdateProjectFinanceInput,
  type UpdateProjectFundedFlagsInput,
  type UpdateProjectInput,
} from '../api/project.api';

export const PROJECT_KEYS = {
  all: ['projects'] as const,
  list: (phase?: ProjectPhase) => ['projects', 'list', phase ?? 'all'] as const,
  paginated: (filters: ProjectListFilters) => ['projects', 'paginated', filters] as const,
  detail: (id: string) => ['projects', id] as const,
  notes: (id: string) => ['projects', id, 'notes'] as const,
  prerequisites: (id: string) => ['projects', id, 'prerequisites'] as const,
  staffingHistory: (id: string) => ['projects', id, 'staffing-history'] as const,
  burnRate: (id: string, granularity: BurnRateGranularity, distributionStrategy: BurnRateDistributionStrategy, from?: string, to?: string) =>
    ['projects', id, 'burn-rate', granularity, distributionStrategy, from ?? '', to ?? ''] as const,
};

// Full flat list — for pickers/dashboards. See ProjectListPage for the paginated list-page hook.
export function useProjects(phase?: ProjectPhase) {
  return useQuery({ queryKey: PROJECT_KEYS.list(phase), queryFn: () => fetchProjects(phase) });
}

// Server-driven pagination — used by ProjectListPage.
// This hook fetches ALL pages of projects matching the server-side filters,
// and applies pagination on the results. All projects including "Closed" are shown
// unless explicitly filtered out by the user.
export function useProjectsPaginated(filters: ProjectListFilters) {
  return useQuery({
    queryKey: PROJECT_KEYS.paginated(filters),
    queryFn: async () => {
      const EXPORT_PAGE_SIZE = 100; // backend's max allowed limit
      const allProjects: IProject[] = [];
      let offset = 0;

      // Fetch all pages of projects from the backend
      for (;;) {
        const page = await fetchProjectsPaginated({
          ...filters,
          limit: EXPORT_PAGE_SIZE,
          offset,
        });
        allProjects.push(...page.items);
        offset += page.items.length;
        if (page.items.length === 0 || offset >= page.total) break;
      }

      // Apply pagination on all results (including "Closed" projects)
      const pageSize = filters.limit ?? 25;
      const pageOffset = filters.offset ?? 0;
      const paginatedProjects = allProjects.slice(pageOffset, pageOffset + pageSize);

      return {
        items: paginatedProjects,
        total: allProjects.length,
      };
    },
    staleTime: 0,
  });
}

export function useProject(id: string) {
  return useQuery({ queryKey: PROJECT_KEYS.detail(id), queryFn: () => fetchProject(id), enabled: Boolean(id) });
}

export function useProjectBurnRate(
  id: string,
  granularity: BurnRateGranularity,
  distributionStrategy: BurnRateDistributionStrategy,
  from?: string,
  to?: string,
) {
  return useQuery({
    queryKey: PROJECT_KEYS.burnRate(id, granularity, distributionStrategy, from, to),
    queryFn: () => fetchProjectBurnRate(id, granularity, distributionStrategy, from, to),
    enabled: Boolean(id),
  });
}

export function useCreateDeliveryProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDeliveryProjectInput) => createDeliveryProject(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.all }),
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectInput }) => updateProject(id, data),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(id) });
    },
  });
}

export function useUpdateProjectFinance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectFinanceInput }) => updateProjectFinance(id, data),
    onSuccess: (_result, { id }) => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(id) }),
  });
}

export function useUpdateProjectFundedFlags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectFundedFlagsInput }) => updateProjectFundedFlags(id, data),
    onSuccess: (_result, { id }) => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(id) }),
  });
}

export function useTransitionProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, toStep }: { id: string; toStep: string }) => transitionProject(id, toStep),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(id) });
    },
  });
}

export function useUpdateStaffingAllocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      userId,
      allocatedHoursPerWeek,
    }: {
      projectId: string;
      userId: string;
      allocatedHoursPerWeek: number | null;
    }) => updateStaffingAllocation(projectId, userId, allocatedHoursPerWeek),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(projectId) }),
  });
}

export function useAddStaffing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: AddStaffingInput }) => addStaffing(projectId, data),
    onSuccess: (_result, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.staffingHistory(projectId) });
    },
  });
}

export function useDestaffUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, userId }: { projectId: string; userId: string }) => destaffUser(projectId, userId),
    onSuccess: (_result, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.staffingHistory(projectId) });
    },
  });
}

export function useEditStaffingDates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, staffingId, data }: { projectId: string; staffingId: string; data: EditStaffingDatesInput }) =>
      editStaffingDates(projectId, staffingId, data),
    onSuccess: (_result, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.staffingHistory(projectId) });
    },
  });
}

export function useStaffingHistory(projectId: string) {
  return useQuery({
    queryKey: PROJECT_KEYS.staffingHistory(projectId),
    queryFn: () => fetchStaffingHistory(projectId),
    enabled: Boolean(projectId),
  });
}

export function useUploadSowAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadSowAttachment(id, file),
    onSuccess: (_result, { id }) => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(id) }),
  });
}

export function useRemoveSowAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeSowAttachment(id),
    onSuccess: (_result, id) => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.detail(id) }),
  });
}

// ── Notes ───────────────────────────────────────────────────────────────────
export function useProjectNotes(projectId: string) {
  return useQuery({
    queryKey: PROJECT_KEYS.notes(projectId),
    queryFn: () => fetchProjectNotes(projectId),
    enabled: Boolean(projectId),
  });
}

export function useCreateProjectNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body: string }) => createProjectNote(projectId, body),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.notes(projectId) }),
  });
}

export function useUpdateProjectNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, noteId, body }: { projectId: string; noteId: string; body: string }) =>
      updateProjectNote(projectId, noteId, body),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.notes(projectId) }),
  });
}

// ── Pre-Requisites ──────────────────────────────────────────────────────────
export function useProjectPrerequisiteStatuses(projectId: string) {
  return useQuery({
    queryKey: PROJECT_KEYS.prerequisites(projectId),
    queryFn: () => fetchProjectPrerequisites(projectId),
    enabled: Boolean(projectId),
  });
}

export function useUpdateProjectPrerequisite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      prerequisiteId,
      data,
    }: {
      projectId: string;
      prerequisiteId: string;
      data: { checked: boolean; note?: string | null };
    }) => updateProjectPrerequisite(projectId, prerequisiteId, data),
    onSuccess: (_result, { projectId }) =>
      queryClient.invalidateQueries({ queryKey: PROJECT_KEYS.prerequisites(projectId) }),
  });
}
